/**
 * Types for Segment-Level Custom Data Tables, mirroring the `SegmentCustomTableResource` API model.
 * Field names and optionality follow the contract exactly, so responses cast without remapping.
 */

import { DbMainExpression } from 'src/models/common/TPESearchRequest';

export enum CDTSegmentType {
    COST_CENTER = 'COST_CENTER',
    ENTITY = 'ENTITY',
    LOCATION = 'LOCATION',
    PRODUCT_LINE = 'PRODUCT_LINE',
    CHANNEL = 'CHANNEL',
    PROJECT = 'PROJECT',
}

export enum ColumnDataType {
    NUMBER = 'NUMBER',
    PERCENTAGE = 'PERCENTAGE',
    TEXT = 'TEXT',
}

export enum ColumnSourceType {
    MANUAL = 'MANUAL',
    PULL_GL = 'PULL_GL',
    DERIVED_PCT = 'DERIVED_PCT',
    DERIVED_BAL = 'DERIVED_BAL',
}

export enum TableType {
    SEGMENT_LEVEL = 'SEGMENT_LEVEL',
    CLI_LEVEL = 'CLI_LEVEL',
}

/** Whether a MANUAL column's entered values persist into later periods. MANUAL columns only. */
export enum PeriodBehavior {
    CARRY_FORWARD = 'CARRY_FORWARD',
    RESET_EACH_PERIOD = 'RESET_EACH_PERIOD',
}

/**
 * A GL pull's status: the execution's own lifecycle value, reported as stored rather than as a category.
 *
 * Lifecycle: PENDING -> READING_CONFIG -> (PULLING_GL | SKIPPING_GL) -> DERIVING -> one of the four
 * terminals. PARTIAL_FAILURE wrote some columns and FAILED wrote none; NO_CONFIG had no GL columns to
 * pull at all, which is not the same claim as a successful pull.
 *
 * **This is a label, not a control-flow input. Never classify it to decide whether a pull has stopped** —
 * read `PullStatusResponse.terminal` (or `SegmentPeriodInfo.lastPullTerminal`), which the server derives from
 * the stored lifecycle's own `isTerminal()`. The reason the answer is reported rather than inferred is that
 * one member makes the inference wrong: SKIPPING_GL reads like an ending and is not one, because the
 * execution has no GL columns to fetch and still runs its derive stage. `isTerminal` in
 * `segmentPullStatus.ts` is the only thing that should touch those fields.
 *
 * **Only a subset occurs per column.** On `overallStatus` and `lastPullStatus` every member is reachable.
 * On `configStatuses` only PENDING, PULLING_GL, SUCCESS and FAILED are ever written, so do not wait for a
 * column to reach one of the other five — and that closed subset is why per-column completion is the one
 * place a small local set is still used, in `isColumnFinished`.
 *
 * An unrecognised value is rendered verbatim rather than recategorised. It needs no terminality guess,
 * because `terminal` already covers members added after this client shipped.
 */
export enum ColumnPullStatus {
    PENDING = 'PENDING',
    READING_CONFIG = 'READING_CONFIG',
    PULLING_GL = 'PULLING_GL',
    SKIPPING_GL = 'SKIPPING_GL',
    DERIVING = 'DERIVING',
    SUCCESS = 'SUCCESS',
    PARTIAL_FAILURE = 'PARTIAL_FAILURE',
    FAILED = 'FAILED',
    NO_CONFIG = 'NO_CONFIG',
}

export enum PeriodType {
    YTD = 'YTD',
    MTD = 'MTD',
    YTDA = 'YTDA',
    MTDA = 'MTDA',
}

export enum ValidationStatus {
    PASS = 'PASS',
    WARNING = 'WARNING',
    FAILED = 'FAILED',
    // Worst-wins rollup uses this when no rule applied, so an un-evaluated report never reads as PASS.
    NOT_APPLICABLE = 'NOT_APPLICABLE',
}

export enum LedgerType {
    PRIMARY = 'PRIMARY',
    SECONDARY = 'SECONDARY',
}

/**
 * One COA data key within a dimension: a STANDARD literal (`value`) or a CUSTOM rollup
 * (`tableId`/`tableName`/`classification`). Mirrors the calculation data-source `COADataKey`.
 */
export interface COADataKey {
    type?: string; // 'STANDARD' | 'CUSTOM'
    value?: string;
    tableId?: string;
    tableName?: string;
    classification?: string;
}

/**
 * Per-COA-dimension data keys for a GL pull. `companies` + `glAccounts` are the required dimensions;
 * `lgCodes` is valid only under a SECONDARY ledger.
 *
 * Both rules are now enforced on both sides. The persistence follow-on has landed, so
 * `SegmentColumnDefinitionFactory.serialisePullCriteria` stores the dimensions,
 * `SegmentColumnConfigValidator.validateDataKeys` checks the required ones and the LG-Code gating, and
 * `GetSegmentDataTableComponent` reads them back. The client checks in `validatePullCriteria` remain because
 * they give an inline message rather than a round-trip failure, but they are no longer the only enforcement.
 *
 * Note the server also rejects more than one key per dimension: the stored criteria document is
 * single-valued, so a second value would be dropped rather than widening the filter.
 */
export interface SegmentDataKeys {
    companies?: COADataKey[];
    glAccounts?: COADataKey[];
    costCenters?: COADataKey[];
    productLines?: COADataKey[];
    locations?: COADataKey[];
    channels?: COADataKey[];
    projects?: COADataKey[];
    lgCodes?: COADataKey[];
}

/**
 * The full GL-pull criteria for a column: a ledger, a period type, and a multi-value data-key selector
 * across the COA dimensions — matching the widened `SegmentPullCriteria` API contract
 * (CR-301897903). A custom rollup is a CUSTOM `COADataKey`, so there is no separate customCoaId.
 */
export interface SegmentPullCriteria {
    ledgerType?: LedgerType;
    periodType?: PeriodType;
    dataKeys?: SegmentDataKeys;
    /** FX conversion type, from the `CURRENCY_CONVERSION_TYPES` list of values (e.g. `Period Average`). */
    fxType?: string;
    /** FX conversion date, `yyyy-MM-dd`. */
    fxDate?: string;
    /** Free-text note carried alongside the column, mirroring a CLI datasource's `description`. */
    description?: string;
}

export interface DerivedConfig {
    type?: string;
    sourceColumnId?: string;
    pctColumnId?: string;
    valueColumnId?: string;
}

export interface ColumnDef {
    columnId?: string;
    displayName?: string;
    dataType?: ColumnDataType;
    sourceType?: ColumnSourceType;
    currency?: string;
    pullCriteria?: SegmentPullCriteria;
    derivedConfig?: DerivedConfig;
    /**
     * Value substituted for a segment absent from this column when tables are joined in a Common Balance
     * Pool. A decimal string, and only meaningful on a NUMBER column. Absent and `'0'` are different
     * claims: a configured `0` asserts a value for that grain rather than standing in for "none set".
     */
    defaultValue?: string;
    /** Whether manually entered values persist into later periods. MANUAL columns only. */
    periodBehavior?: PeriodBehavior;
}

export interface SegmentTableDefinition {
    tableId?: string;
    tableName?: string;
    tableType?: TableType;
    segmentType?: CDTSegmentType;
    segmentCustomCoaId?: string;
    refreshCadence?: string;
    status?: string;
    columns?: ColumnDef[];
    owner?: string;
    createdDate?: number;
    lastUpdatedDate?: number;
    /**
     * When the rows of the period this describes last changed, epoch millis — the requested `period`, or
     * the current open period when none was requested. Moves on any write to a row, so it is NOT
     * `SegmentAggregates.lastUpdated`, which tracks pull timestamps only and does not move on an upload.
     * Distinct from `lastUpdatedDate` above, which is the table's own metadata timestamp.
     */
    dataLastUpdated?: number;
}

/**
 * Identified by `segmentValue`; there is no row id. `segmentDescription` is a
 * row-level FRAMES field, not an entry in `columns` — it has no source type.
 * `values` holds NUMBER/PERCENTAGE cells, `textValues` holds TEXT, both keyed by `columnId`.
 */
export interface SegmentRow {
    segmentValue?: string;
    segmentDescription?: string;
    values?: Record<string, number>;
    textValues?: Record<string, string>;
    sourceType?: Record<string, string>;
    lastPullTimestamp?: Record<string, number>;
    status?: string;
}

/** Server-computed period totals, keyed by `columnId`. Never summed client-side. */
export interface SegmentAggregates {
    aggregatedTotal?: Record<string, number>;
    unallocatedAmount?: Record<string, number>;
    rowCount?: number;
    lastUpdated?: number;
}

/**
 * One pull's progress, reported as the execution's own lifecycle value rather than as a category — so it
 * says which stage a running pull has reached and which of the four outcomes a finished one had.
 */
export interface PullStatusResponse {
    pullRequestId?: string;
    /**
     * The execution's status; every `ColumnPullStatus` member is reachable here. Absent only when the
     * server states no status at all, which means "not stated", never "not started".
     */
    overallStatus?: ColumnPullStatus;
    /**
     * Whether the pull has stopped. **Poll on this, never on `overallStatus`.**
     *
     * The server derives it from the stored lifecycle's own `isTerminal()`, so no client has to know which
     * members are endings — and specifically none has to know that `SKIPPING_GL` reads like one and is not.
     * Absent means the record stated no status at all; read that as not terminal. `isTerminal` in
     * `segmentPullStatus.ts` is the only thing that should touch this field.
     */
    terminal?: boolean;
    /**
     * The same, per columnId. Only PENDING, PULLING_GL, SUCCESS and FAILED are ever written here.
     */
    configStatuses?: Record<string, ColumnPullStatus>;
    /**
     * Why a column failed, keyed by columnId — plus reserved keys (`execution`, `workflow`) for a failure
     * of the run as a whole, which also arrives on its own as `failureReason`. Iterate this as well as
     * `configStatuses` when listing columns: a column can have a reason with no status.
     */
    errors?: Record<string, string>;
    /**
     * Why the run as a whole failed, when no single column is to blame (its workflow timed out, a stage
     * failed hard, or it never started). The server's own text; absent for a success and for a pull that
     * failed only in specific columns.
     */
    failureReason?: string;
    rowsUpserted?: number;
    startedAt?: number;
    completedAt?: number;
    /**
     * The FX rate the pull applied, per columnId. The literal `MULTIPLE` when one column's pull returned
     * more than one distinct rate, which is why it is a string rather than a number.
     */
    fxRates?: Record<string, string>;
}

export interface ValidationEntry {
    type?: string;
    status?: ValidationStatus;
    // Present on column-level entries; absent on table/row-level ones.
    columnId?: string;
    message?: string;
    details?: string;
}

export interface ValidationReport {
    overallStatus?: ValidationStatus;
    validations?: ValidationEntry[];
    lastValidatedAt?: number;
}

/**
 * One selectable period, and what is durably known about its last GL pull.
 *
 * No `rowCount` and no `lastUpdated`: GetSegmentPeriods derives this list from the per-period LEGEND rows and
 * so reports neither. Nothing read `rowCount`; the data timestamp is now `SegmentTableDefinition.dataLastUpdated`,
 * for the period being viewed. The grid's own row counts come from `SegmentAggregates.rowCount` (the footer
 * total) and `SearchSegmentDataTableRows.totalSize` (the pager), neither of which this list ever fed.
 *
 * The `lastPull*` fields survive a reload, unlike the polled `PullStatusResponse` — which needs an id the
 * page no longer has. They come from the pull's own execution records rather than from the period's rows,
 * which is why they are unaffected by the list no longer reading them. They are absent together when no pull
 * has ever run for the period, which is the ordinary state of a period populated by upload; absent is never
 * evidence that nothing was pulled.
 */
export interface SegmentPeriodInfo {
    period?: string;
    status?: string;
    /** The most recently started pull's lifecycle value, for display. */
    lastPullStatus?: ColumnPullStatus;
    /**
     * Whether that pull has stopped — false means one is running for this period right now. Same derivation
     * and same rules as `PullStatusResponse.terminal`; read it rather than classifying `lastPullStatus`, so
     * the two surfaces cannot describe one stored value differently.
     */
    lastPullTerminal?: boolean;
    /** That pull's id, so its per-column detail can be read back after a reload. */
    lastPullRequestId?: string;
    lastPullStartedAt?: number;
    /** Absent while it is still running, so the two timestamps together say which state it is in. */
    lastPullCompletedAt?: number;
    /** `UI` for a user-initiated pull, `MEC` for month-end close. */
    lastPullSource?: string;
}

// --- Requests and responses ---

export interface CreateSegmentDataTableRequest {
    tableName: string;
    // tableType is intentionally not sent: CR-299438935 dropped it from the request and the backend
    // hardcodes SEGMENT_LEVEL. The TableType enum is still used by the definition/response types below.
    segmentType: CDTSegmentType;
    segmentCustomCoaId?: string;
    refreshCadence?: string;
    columns: ColumnDef[];
}

export interface CreateSegmentDataTableResponse {
    tableId?: string;
    tableName?: string;
    tableType?: TableType;
    segmentType?: CDTSegmentType;
    columns?: ColumnDef[];
}

/**
 * Adds one or more columns to an existing segment data table. The target period is resolved
 * server-side (the current open period) and is deliberately not a caller input, so a column cannot
 * be added to a closed period. A derived column may reference a column the table already has; the
 * backend resolves references across the union of existing and new columns.
 */
export interface AddSegmentColumnsRequest {
    tableId: string;
    columns: ColumnDef[];
}

/**
 * The full effective column set after the add, not just the columns added, so a client can
 * re-render the grid from this response alone.
 */
export interface AddSegmentColumnsResponse {
    tableId?: string;
    columns?: ColumnDef[];
}

export interface GetSegmentDataTableResponse {
    table?: SegmentTableDefinition;
}

/** Omitting both filters returns every segment table. */
export interface ListSegmentDataTablesRequest {
    segmentType?: CDTSegmentType;
    tableType?: TableType;
}

export interface ListSegmentDataTablesResponse {
    tables?: SegmentTableDefinition[];
}

/**
 * `filterExpression` reuses the existing ATP `DbMainExpression`, so the CDT filter builder applies
 * unchanged. The backend supports both numbered paging (`page`, 1-based; the response echoes the
 * `totalSize` needed for a page count) and a `lastEvaluatedKey` cursor. The detail viewer uses the
 * numbered form, mirroring the CLI custom-data-tables records grid.
 */
export interface SearchSegmentDataTableRowsRequest {
    tableId: string;
    period: string;
    page?: number;
    pageSize?: number;
    lastEvaluatedKey?: string;
    filterExpression?: DbMainExpression;
    sortField?: string;
    sortOrder?: string;
}

export interface SearchSegmentDataTableRowsResponse {
    tableId?: string;
    period?: string;
    rows?: SegmentRow[];
    lastEvaluatedKey?: string;
    hasMore?: boolean;
    totalRowsInPeriod?: number;
    /** The page and page size actually served, after defaulting/capping. */
    page?: number;
    pageSize?: number;
    /** Exact count of rows matching the request across every page — feeds the numbered pager. */
    totalSize?: number;
}

export interface SaveSegmentDataTableResponse {
    tableId?: string;
    period?: string;
    rowsUpdated?: number;
    validationReport?: ValidationReport;
}

/**
 * `period` is resolved server-side (the current open period) and echoed here — not sent in the request.
 * A refused upload still returns 200 with these counters + a validationReport, so read them either way.
 */
export interface UploadSegmentDataTableResponse {
    tableId?: string;
    period?: string;
    rowsUploaded?: number;
    rowsMatched?: number;
    valuesChanged?: number;
    nonEditableColumnsRejected?: number;
    unknownSegmentsRejected?: number; // null when the segment master could not be read
    unknownSegmentsCheckStatus?: ValidationStatus; // PASS | FAILED | NOT_APPLICABLE
    valuesOutOfBounds?: number;
    validationReport?: ValidationReport;
}

export interface DownloadSegmentDataTableResponse {
    downloadUrl: string;
}

/**
 * A presigned URL for the prefilled upload template: the table's manual columns only, each cell
 * carrying its current value for the period, with the segment key and description locked. Distinct
 * from DownloadSegmentDataTable (the three-sheet review workbook); this artifact is the input to
 * UploadSegmentDataTable.
 */
export interface DownloadSegmentUploadTemplateResponse {
    downloadUrl: string;
}

export interface GetCDTGLPullStatusResponse {
    pullStatus?: PullStatusResponse;
}

export interface GetSegmentAggregatesResponse {
    tableId?: string;
    period?: string;
    aggregates?: SegmentAggregates;
}

export interface GetSegmentValidationsResponse {
    tableId?: string;
    period?: string;
    report?: ValidationReport;
}

export interface GetSegmentPeriodsResponse {
    tableId?: string;
    periods?: SegmentPeriodInfo[];
}

export interface TriggerCDTGLPullResponse {
    pullRequestId: string;
}
